import { Get, useUserContext } from '@talos/kyoko';
import { throttle } from 'lodash-es';
import { createContext, useCallback, useContext, type PropsWithChildren } from 'react';

export const FeesContext = createContext<FeesContextProps | undefined>(undefined);
FeesContext.displayName = 'FeesContext';

/*
  Effective fees are those used by the OMS
  Exchange fees are those specified by a gateway to the exchange
  Custom fees are those overwritten by users
*/
export interface FeesContextProps {
  listMarketFees: () => Promise<Fee[]>;
  listCustomFees: () => Promise<Fee[]>;
  listEffectiveFees: () => Promise<Fee[]>;
}

export function useFees() {
  const context = useContext(FeesContext);
  if (context === undefined) {
    throw new Error('Missing FeesContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

/**
 * As to why these are throttled:
 * Backend historically has a high number of requests from UI for /v1/fees/markets  and /v1/fees/configs (which slowed down to service large number of fee events ~21K)
 * Backend notes running into this issue in the past, which led to throttling and rejecting requests if they were not able to service them,
 * So backend does not crash luckily, but it cant handle all requests we send.
 * The underlying reason (useEffects reruns until successful) can be fixed in the UI, but this is a good bandaid.
 * We won't stop until backend responds with data, which won't happen unless backend can service it (thus we throttle it).
 */
export const FeesProvider = function FeesProvider({ children }: PropsWithChildren) {
  const { orgApiEndpoint } = useUserContext();

  const listMarketFees = useCallback(() => {
    return orgApiEndpoint ? throttledGetMarketFees(orgApiEndpoint) : Promise.reject('Missing orgApiEndpoint');
  }, [orgApiEndpoint]);

  const listCustomFees = useCallback(() => {
    return orgApiEndpoint ? throttledGetCustomFees(orgApiEndpoint) : Promise.reject('Missing orgApiEndpoint');
  }, [orgApiEndpoint]);

  const listEffectiveFees = useCallback(() => {
    return orgApiEndpoint ? throttledGetEffectiveFees(orgApiEndpoint) : Promise.reject('Missing orgApiEndpoint');
  }, [orgApiEndpoint]);

  return (
    <FeesContext.Provider value={{ listMarketFees, listCustomFees, listEffectiveFees }}>
      {children}
    </FeesContext.Provider>
  );
};

export interface Fee {
  Symbol?: string;
  MarketAccount: string;
  MakerFeeRate: string;
  TakerFeeRate: string;
}

const throttledGetMarketFees = throttle(
  (endpoint: string) => Get(endpoint, `/fees/markets`).then(results => results.data as Fee[]),
  500,
  {
    trailing: true,
    leading: true,
  }
);
const throttledGetCustomFees = throttle(
  (endpoint: string) => Get(endpoint, `/fees/configs`).then(results => results.data as Fee[]),
  500,
  {
    trailing: true,
    leading: true,
  }
);
const throttledGetEffectiveFees = throttle(
  (endpoint: string) => Get(endpoint, `/fees`).then(results => results.data as Fee[]),
  500,
  {
    trailing: true,
    leading: true,
  }
);
